import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Heart, Star, ShoppingCart, Minus, Plus, Lock } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { fmt, unitName, pluralUnit } from "@/lib/api";
import { PrimeLockButton } from "@/components/ui/PrimeLockButton";

export function Stars({ value = 0, size = 13 }) {
  return (
    <span className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(value) ? "fill-amber-500 text-amber-500" : "fill-neutral-300 text-neutral-300 dark:fill-neutral-700 dark:text-neutral-700"}
        />
      ))}
    </span>
  );
}

export function StockPill({ status }) {
  const map = {
    in_stock: { label: "In Stock", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500 dark:text-emerald-400" },
    low_stock: { label: "Low Stock", cls: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400" },
    out_of_stock: { label: "Out of Stock", cls: "border-red-500/30 bg-red-500/10 text-red-500 dark:text-red-400" },
  };
  const s = map[status] || map.in_stock;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>
      <span className="h-1 w-1 rounded-full bg-current" />
      {s.label}
    </span>
  );
}

export default function ProductCard({ product, index = 0, primeChannel = false }) {
  const navigate = useNavigate();
  const { addItem, wishlistIds, toggleWishlist, primeActive, user } = useStore();
  const wished = wishlistIds.includes(product.id);
  const out = product.status === "out_of_stock";
  const moq = product.min_order_qty || 1;       // steps follow the MOQ — never +1
  const [qty, setQty] = useState(moq);

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: Math.min(index * 0.05, 0.4) }}
      className="group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/30 hover:shadow-lift"
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/shop/product/${product.id}${primeChannel ? "?channel=prime" : ""}`)}
    >
      <div className="relative aspect-square overflow-hidden bg-white">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-[1.05]"
        />
        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          {product.discount_pct > 0 && (
            <span className="rounded-md bg-amber-500 px-2 py-1 text-[10px] font-bold text-black" data-testid={`product-discount-${product.id}`}>
              {product.discount_pct}% OFF
            </span>
          )}
          {product.new_arrival && (
            <span className="rounded-md border border-emerald-500/40 bg-black/60 px-2 py-1 text-[10px] font-bold text-emerald-400 backdrop-blur">
              NEW
            </span>
          )}
        </div>
        <button
          data-testid={`product-wishlist-${product.id}`}
          onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
          className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur transition-all duration-200 active:scale-90 ${
            wished
              ? "border-amber-500/60 bg-amber-500 text-black"
              : "border-strong bg-surface/80 text-2 hover:border-amber-500/60 hover:text-amber-500"
          }`}
          aria-label="Toggle wishlist"
        >
          <Heart className={`h-4 w-4 transition-transform duration-200 ${wished ? "scale-110 fill-black" : ""}`} />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-[10px] font-bold uppercase tracking-[0.18em] text-3">
            {product.brand_name || product.category}
          </p>
          <StockPill status={product.status} />
        </div>
        <h3 className="line-clamp-2 min-h-[2.5rem] font-display text-base font-medium leading-snug tracking-tight text-1">
          {product.name}
        </h3>
        <div className="flex items-center gap-1.5">
          <Stars value={product.rating_avg} />
          <span className="text-xs font-semibold text-2">{product.rating_avg}</span>
          <span className="text-xs text-3">({product.rating_count})</span>
        </div>
        <div className="flex items-baseline gap-2">
          {primeChannel && product.prime_price && product.regular_price && product.prime_price < product.regular_price ? (
            <>
              {/* PRIME Store: member price is the primary price, shown to everyone */}
              <span className="font-display text-lg font-semibold text-[#A96C1E]" data-testid={`prime-card-price-${product.id}`}>
                {fmt(product.prime_price)}<span className="text-xs font-medium text-3">/{unitName(product.unit)}</span>
              </span>
              <span className="rounded-full border border-[#E4B66D]/50 bg-[#242521] px-2 py-0.5 text-[9px] font-extrabold text-[#E4B66D]" data-testid="prime-card-chip">PRIME</span>
              <span className="text-xs text-3 line-through" data-testid={`prime-card-regular-${product.id}`}>{fmt(product.regular_price)}</span>
              <span className="text-[10px] font-bold text-emerald-600" data-testid="prime-card-saving">
                Save {fmt(product.regular_price - product.prime_price)}
              </span>
            </>
          ) : (
            <>
              <span className="font-display text-lg font-semibold text-[#A96C1E]">
                {fmt(product.price)}<span className="text-xs font-medium text-3">/{unitName(product.unit)}</span>
              </span>
              {product.compare_at_price && product.compare_at_price > product.price && (
                <span className="text-xs text-3 line-through">{fmt(product.compare_at_price)}</span>
              )}
            </>
          )}
        </div>
        <p className="text-[11px] font-semibold text-3" data-testid={`product-moq-${product.id}`}>
          {moq > 1 ? `MOQ: ${moq.toLocaleString("en-IN")} ${pluralUnit(product.unit, moq)} · steps of ${moq.toLocaleString("en-IN")}` : `Sold per ${unitName(product.unit)}`}
        </p>
        {primeChannel && !primeActive && (
          <p className="mt-1 text-[11px] font-bold text-[#8A6A2F]" data-testid="prime-card-teaser">
            Member price — join PRIME to purchase
          </p>
        )}

        <div className="mt-auto flex items-stretch gap-2 pt-2">
          {primeChannel && !primeActive ? (
            <PrimeLockButton
              compact
              testid={`product-locked-${product.id}`}
              top="Members only"
              bottom="Join PRIME to Purchase"
              onClick={(e) => { e.stopPropagation(); navigate(user ? "/prime" : "/login?redirect=/prime"); }}
            />
          ) : (
            <>
          {!out && moq > 1 && (
            <div className="flex items-center rounded-lg border border-line" data-testid={`product-stepper-${product.id}`}>
              <button
                data-testid={`product-minus-${product.id}`}
                onClick={(e) => { e.stopPropagation(); setQty((q) => Math.max(moq, q - moq)); }}
                disabled={qty <= moq}
                aria-label="Decrease quantity"
                className="flex h-11 w-9 items-center justify-center text-2 transition-colors hoverable-accent disabled:opacity-30"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-9 text-center text-xs font-bold text-1" data-testid={`product-qty-${product.id}`}>{qty.toLocaleString("en-IN")}</span>
              <button
                data-testid={`product-plus-${product.id}`}
                onClick={(e) => { e.stopPropagation(); setQty((q) => q + moq); }}
                aria-label="Increase quantity"
                className="flex h-11 w-9 items-center justify-center text-2 transition-colors hoverable-accent"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <button
            data-testid={`product-add-${product.id}`}
            disabled={out}
            onClick={(e) => {
              e.stopPropagation();
              addItem(product.id, product.default_sku, qty, { channel: primeChannel ? "prime" : "regular" });
              setQty(moq);
            }}
            className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-full text-sm font-bold transition-all ${
              out
                ? "cursor-not-allowed border border-line text-3"
                : "bg-[#20221F] text-[#F8F6F0] shadow-[0_3px_12px_rgba(32,34,31,0.22)] hover:bg-[#2E302C]"
            }`}
          >
            <ShoppingCart className="h-4 w-4" />
            {out ? "Unavailable" : "Add"}
          </button>
            </>
          )}
        </div>
      </div>
    </motion.article>
  );
}
